import * as React from "react";

export type CollaborationMode = "ai-agent-chat";

interface CollaborationBarContext {
  /** Whether the collaboration bar is open (expanded). */
  isOpen: boolean;

  /** The currently active collaboration mode, or null if closed. */
  activeMode: CollaborationMode | null;

  /** Close (collapse) the collaboration bar. */
  close: () => void;

  /** Set the active mode (and open the bar if it's closed). */
  setActiveMode: (mode: CollaborationMode | null) => void;
}

/**
 * Context for the collaboration bar (right sidebar).
 *
 * Use `useCollaborationBar` to access the context.
 */
export const CollaborationBarContext =
  React.createContext<CollaborationBarContext | null>(null);

/**
 * Custom hook to access the collaboration bar context.
 *
 * Usage:
 * ```typescript
 * const collaborationBar = useCollaborationBar();
 *
 * collaborationBar.activeMode;
 * collaborationBar.close();
 * collaborationBar.setActiveMode('ai-agent-chat');
 * ```
 */
export function useCollaborationBar(): CollaborationBarContext {
  const context = React.useContext(CollaborationBarContext);

  if (!context) {
    throw new Error(
      "useCollaborationBar must be used within CollaborationBarProvider",
    );
  }

  return context;
}
